/**
 * Static content for the "Execute the Religious Covenant" workflow.
 *
 * Source of the service families and services: the member library page
 * /support/library/stewardship-services-catalog
 * Source of the execution steps: /support/library/executing-religious-covenant
 * Source of the authorization formats: /support/library/entity-authorization-resolutions
 *
 * Nothing here restates the covenant contract's own operative text. The covenant
 * itself comes from the member's packet.
 */

export type EntityType = 'llc' | 'corporation' | 'legacy';

export interface EntityOption {
  value: EntityType;
  label: string;
  helper: string;
}

export const ENTITY_OPTIONS: EntityOption[] = [
  {
    value: 'llc',
    label: 'A wholly owned LLC',
    helper:
      'The Association is the member. It adopts through its Officer. The authorization is written as a member resolution.',
  },
  {
    value: 'corporation',
    label: 'A corporation',
    helper:
      'The board adopts at a meeting its bylaws recognize, and the meeting is minuted. The authorization is written as a board resolution.',
  },
  {
    value: 'legacy',
    label: 'An existing or legacy entity joining now',
    helper:
      'An entity that has been operating for a while and is coming in now. It adopts in the voice its own governing papers use, and the true signing date is the strong one.',
  },
];

export interface ServiceFamily {
  key: string;
  label: string;
  blurb: string;
  services: { id: string; label: string; hint: string }[];
}

export const SERVICE_FAMILIES: ServiceFamily[] = [
  {
    key: 'accountability',
    label: 'Accountability',
    blurb: 'Regular review of how the entity and its people are actually doing what they said they would do.',
    services: [
      {
        id: 'accountability_sessions',
        label: 'Stewardship accountability sessions',
        hint: 'Regular structured review of an entity or a steward against stated commitments.',
      },
      {
        id: 'financial_accountability_review',
        label: 'Financial accountability review',
        hint: 'Periodic look at the books, the cadence, and how well separate things are kept separate.',
      },
      {
        id: 'annual_examination',
        label: 'Annual stewardship examination',
        hint: 'The deep yearly review, including preparing the annual review materials.',
      },
      {
        id: 'counsel_of_conscience',
        label: 'Counsel of conscience',
        hint: 'Scheduled counsel on stewardship decisions, recorded as held.',
      },
    ],
  },
  {
    key: 'governance',
    label: 'Governance',
    blurb: 'Keeping the decisions, the papers, and the leadership of the entity in good order.',
    services: [
      {
        id: 'governance_administration',
        label: 'Governance administration',
        hint: 'Drafting and keeping resolutions, minutes, and the governance log.',
      },
      {
        id: 'policy_counsel',
        label: 'Policy counsel',
        hint: 'Writing and reviewing the entity operating papers and policies.',
      },
      {
        id: 'officer_advisory',
        label: 'Officer and manager advisory',
        hint: 'Structured advice to the people leading the entity.',
      },
      {
        id: 'records_integrity',
        label: 'Records integrity services',
        hint: 'Custody of documents, inventory of originals, and retention.',
      },
    ],
  },
  {
    key: 'administration',
    label: 'Administration',
    blurb: 'The day to day work of keeping books, paying and receiving, and caring for what is held.',
    services: [
      {
        id: 'books_and_records',
        label: 'Books and records maintenance',
        hint: 'Ledgers, journals, registers, and reconciliations.',
      },
      {
        id: 'accounts_administration',
        label: 'Accounts administration',
        hint: "Handling payments, receipts, and correspondence from the entity's own funds.",
      },
      {
        id: 'property_stewardship',
        label: 'Property stewardship',
        hint: 'Care, security, upkeep, and readiness of held property, by location.',
      },
      {
        id: 'vendor_coordination',
        label: 'Vendor and obligation coordination',
        hint: 'Lining up and following through with outside providers.',
      },
      {
        id: 'reporting_services',
        label: 'Reporting services',
        hint: 'Regular reports to owners, boards, or members.',
      },
    ],
  },
  {
    key: 'formation',
    label: 'Formation',
    blurb: 'Teaching and training the people, so the practice is carried and not just written down.',
    services: [
      {
        id: 'instruction',
        label: 'Instruction in stewardship doctrine and practice',
        hint: 'Teaching sessions, recorded as held.',
      },
      {
        id: 'training_administration',
        label: 'Training administration',
        hint: 'Designing and giving the yearly training and role formation.',
      },
      {
        id: 'study_facilitation',
        label: 'Study and curriculum facilitation',
        hint: 'Ongoing formation programs for members and households.',
      },
      {
        id: 'onboarding_formation',
        label: 'Onboarding formation',
        hint: 'Bringing new members, officers, or workers into the practice.',
      },
    ],
  },
  {
    key: 'community',
    label: 'Community',
    blurb: 'Gathering the people and caring for them together.',
    services: [
      {
        id: 'convening',
        label: 'Convening services',
        hint: 'Organizing and running observances, gatherings, and meetings.',
      },
      {
        id: 'member_care',
        label: 'Member care coordination',
        hint: 'Running the needs and benefits processes.',
      },
      {
        id: 'mission_coordination',
        label: 'Service and mission coordination',
        hint: 'Organizing the outward service work of the community.',
      },
      {
        id: 'hospitality',
        label: 'Hospitality administration',
        hint: 'Provisioning and running gatherings and shared spaces.',
      },
    ],
  },
];

export const SERVICE_LOOKUP: Record<string, { family: string; label: string }> = SERVICE_FAMILIES.reduce(
  (acc, fam) => {
    fam.services.forEach((s) => {
      acc[s.id] = { family: fam.label, label: s.label };
    });
    return acc;
  },
  {} as Record<string, { family: string; label: string }>,
);

export interface SelectedService {
  id: string;
  family: string;
  label: string;
  description: string;
  performed_by: string;
  fee_basis: string;
  fee_amount: string;
  reasonableness: string;
  stranger_test: string;
}

export interface AffirmationSigner {
  id: string;
  name: string;
  capacity: string;
}

export type CadenceType = 'fixed_day' | 'percentage';

export interface CovenantWorkflowData {
  entity_type?: EntityType;
  entity_legal_name?: string;
  signer_name?: string;
  signer_capacity?: string;
  association_officer_name?: string;
  affirmation_signers?: AffirmationSigner[];
  services?: SelectedService[];
  contribution_only?: boolean;
  cadence_type?: CadenceType;
  cadence_day?: '1' | '15' | 'month_end';
  cadence_percentage?: string;
  initial_transition?: string;
  covenant_date?: string;
}

export const CADENCE_DAY_OPTIONS: { value: '1' | '15' | 'month_end'; label: string }[] = [
  { value: '1', label: 'The first day of each month' },
  { value: '15', label: 'The fifteenth day of each month' },
  { value: 'month_end', label: 'The last day of each month' },
];

export function cadenceSentence(data: CovenantWorkflowData): string {
  if (data.cadence_type === 'percentage') {
    const pct = data.cadence_percentage || '[percentage]';
    return `A stated share of ${pct} percent, settled at the end of each month.`;
  }
  const found = CADENCE_DAY_OPTIONS.find((o) => o.value === data.cadence_day);
  return found ? `${found.label}.` : 'A fixed day of each month.';
}
